"use client";

import React from "react";
import { Calendar, User, Tag, ArrowRight, Share2, Clock } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";



function RecommendedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="10535" className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-4" data-index={i}>
            <div className="bg-muted h-48 w-full animate-pulse rounded-lg" />
            <div className="bg-muted h-6 w-3/4 animate-pulse rounded" />
            <div className="bg-muted h-4 w-full animate-pulse rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} className="group overflow-hidden border-none shadow-sm transition-all hover:shadow-md" data-index={i}>
          <div className="relative overflow-hidden">
            <AspectRatio ratio={16 / 9}>
              <img
                src={post.featuredImage || "/img/blog_placeholder_generic.png"}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </AspectRatio>
          </div>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Calendar className="h-3 w-3" />
              <span>{post.date ? new Date(post.date).toLocaleDateString() : "Recent Update"}</span>
            </div>
            <CardTitle className="line-clamp-2 text-lg group-hover:text-primary transition-colors">
              <Link to={post.link || "#"}>{post.title}</Link>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className="line-clamp-3 text-sm text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
            />
          </CardContent>
          <CardFooter>
            <Link to={post.link || "#"} className="w-full">
              <Button variant="outline" className="w-full cursor-pointer justify-between rounded-md group-hover:border-primary group-hover:text-primary">
                Read Full Article <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RecommendedPostsWrapper() {
  
  
  const recommendedQuery = new WP_Query({
    per_page: 3,
    ignore_sticky: true,
    orderby: 'date',
    order: 'desc'
  });

  return (
    <div className="mt-16 bg-muted/50 py-16">
      <div className="container mx-auto px-4">
        <div className="mb-10 flex items-center justify-between">
          <h3 className="font-sans text-3xl font-bold text-foreground">Latest Industry Insights</h3>
          <Link to="#">
            <Button variant="ghost" className="rounded-md hover:text-primary">All Technical Updates</Button>
          </Link>
        </div>
        <PostsProvider wp_query={{ per_page: 3, ignore_sticky: true, orderby: "date", order: "desc" }}>
          <RecommendedPostsConsumer />
        </PostsProvider>
      </div>
    </div>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-20">
        <div className="space-y-8">
          <div className="bg-muted h-12 w-3/4 animate-pulse rounded-lg" />
          <div className="bg-muted h-6 w-1/2 animate-pulse rounded-lg" />
          <div className="bg-muted aspect-video w-full animate-pulse rounded-lg" />
          <div className="space-y-4">
            <div className="bg-muted h-4 w-full animate-pulse rounded" />
            <div className="bg-muted h-4 w-full animate-pulse rounded" />
            <div className="bg-muted h-4 w-5/6 animate-pulse rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-destructive">Error loading technical data</h2>
        <p className="text-muted-foreground mb-6">{error}</p>
        <Button onClick={() => refetch()} className="rounded-md">Try Again</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-foreground">Report not found</h2>
        <p className="text-muted-foreground mb-6">The technical publication you are looking for is currently unavailable.</p>
        <Link to="/HomePage">
          <Button className="rounded-md">Return Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const authorName = post.author?.name || "Custom RTD Editorial";
  const authorAvatar = post.author?.avatar?.["96"] || "";
  const category = post.categories?.[0];

  return (
    <article className="min-h-screen bg-background">
      {}
      <header className="container mx-auto max-w-4xl px-4 pt-12 pb-8 md:pt-20">
        <div className="mb-6 flex flex-wrap items-center gap-4 text-sm font-medium text-muted-foreground">
          {category && (
            <span className="bg-primary/10 text-primary rounded-md px-3 py-1">
              {category.name}
            </span>
          )}
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <span>{post.date ? new Date(post.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : ""}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            <span>6 min technical read</span>
          </div>
        </div>

        <h1 className="font-sans text-4xl font-bold leading-tight tracking-tight text-foreground md:text-5xl lg:text-6xl">
          {post.title}
        </h1>

        <div className="mt-8 flex items-center justify-between border-y border-border py-6">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border border-border">
              <AvatarImage src={authorAvatar} alt={authorName} />
              <AvatarFallback><User className="h-5 w-5" /></AvatarFallback>
            </Avatar>
            <div>
              <p className="text-sm font-semibold text-foreground">{authorName}</p>
              <p className="text-xs text-muted-foreground">Lead Formulator</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="icon" className="rounded-md text-muted-foreground hover:text-primary">
              <Share2 className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="container mx-auto max-w-5xl px-4 pb-12">
          <div className="overflow-hidden rounded-lg shadow-md">
            <AspectRatio ratio={16 / 9}>
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover"
              />
            </AspectRatio>
          </div>
        </div>
      )}

      {}
      <div className="container mx-auto max-w-3xl px-4 pb-16">
        <div
          className="prose prose-lg prose-slate dark:prose-invert max-w-none
            prose-headings:font-sans prose-headings:font-bold prose-headings:tracking-tight
            prose-a:text-primary prose-a:no-underline hover:prose-a:underline
            prose-img:rounded-lg prose-img:shadow-md
            [&>p]:mb-6 [&>p]:leading-relaxed [&>h2]:mt-12 [&>h2]:mb-6 [&>h3]:mt-8 [&>h3]:mb-4
            [&>ul]:mb-6 [&>ul]:list-disc [&>ul]:pl-6 [&>ol]:mb-6 [&>ol]:list-decimal [&>ol]:pl-6
            [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:text-muted-foreground"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />

        {}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-12 pt-8">
            <h4 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              <Tag className="h-4 w-4" /> Industry Categories
            </h4>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span
                  key={tag.id}
                  className="bg-secondary/50 text-secondary-foreground hover:bg-secondary cursor-default rounded-md px-3 py-1 text-sm transition-colors"
                >
                  #{tag.name}
                </span>
              ))}
            </div>
          </div>
        )}

        <Separator className="my-12" />

        {}
        <div className="bg-card rounded-lg border border-border p-8 shadow-sm">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
            <Avatar className="h-20 w-20 border-2 border-background shadow-sm">
              <AvatarImage src={authorAvatar} alt={authorName} />
              <AvatarFallback className="text-lg"><User className="h-8 w-8" /></AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-foreground">{authorName}</h3>
                <Button variant="outline" size="sm" className="rounded-md">Follow Updates</Button>
              </div>
              <p className="text-muted-foreground">
                {post.author?.description || "Expert in custom formulation and large-scale canning operations for functional wellness beverages and probiotic nutraceuticals."}
              </p>
            </div>
          </div>
        </div>
      </div>

      {}
      <RecommendedPostsWrapper />
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  
  const currentWPQueryParams = (typeof window !== 'undefined' && (window as any).wvcClient?.getCurrentWPQueryParams()) || {};
  
  
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background w-full">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}